// browserPhone.ts · v12.10.30 (#1155 #1156 #1157 the browser phone - one Twilio Voice device per signed-in person, call back, live transfers, hold sound) · 2026-11-03 · VoiceSpectrum AI
//
// #1155 (Tushar 10/3): "i think i will prefer through browser." Staff call patients back FROM THE BROWSER (Twilio Voice JavaScript SDK),
// and the same browser phone receives the AI's live transfers (#1157). Built once, shared by both - this file is the engine; the screens
// (components/BrowserPhone.tsx) only read its state and press its buttons.
//
// OFF unless the practice's Call back feature switch is on (Support Ops › Features, #2256 - ships off) AND the Twilio account has its
// API key + TwiML App (T4 › Provider registry). The SDK is VENDORED (public/vendor/twilio-voice-2.18.5.min.js, Apache-2.0 - the build PC
// installs from the lock file only) and is loaded LAZILY: the script tag is added only when the feature is on and the person presses
// Call back, or is a Handoff person (so transfers can ring them).
//
// State is a module singleton with subscribe() so every screen sees the same call ("it survives moving between screens", #1155).
import api, { ApiError, buildUrl } from './api';

// ── The few Twilio SDK shapes we use (the vendored UMD sets window.Twilio.Device). ──
interface TwCall {
  parameters: Record<string, string>;
  customParameters?: Map<string, string>;
  on(event: string, cb: (...args: unknown[]) => void): void;
  accept(opts?: unknown): void;
  reject(): void;
  disconnect(): void;
  mute(should?: boolean): void;
  isMuted(): boolean;
  sendDigits(d: string): void;
  status(): string;
}
interface TwAudioProcessor { createProcessedStream(stream: MediaStream): Promise<MediaStream>; destroyProcessedStream(stream: MediaStream): Promise<void> }
interface TwDevice {
  state: string;
  audio?: { addProcessor(p: TwAudioProcessor, isRemote?: boolean): Promise<void> };
  on(event: string, cb: (...args: unknown[]) => void): void;
  register(): Promise<void>;
  unregister(): Promise<void>;
  connect(opts: { params: Record<string, string> }): Promise<TwCall>;
  updateToken(t: string): void;
  destroy(): void;
}
interface TwGlobal { Device: new (token: string, opts?: Record<string, unknown>) => TwDevice }

export const SDK_URL = `${(import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/'}vendor/twilio-voice-2.18.5.min.js`;

export interface PhoneStatus {
  featureOn: boolean;
  configured: boolean;
  reason?: string | null;
  identity?: string | null;
  inHandoff?: boolean;
  ringStrategy?: string;
  ringSec?: number;
}

/** The call window's states (prototype v1.11): dialing 1a · ringing 1b · connected 1c · machine 3b · vmleave (3b → Leave voicemail) ·
 *  noanswer 3a · ended 1d · mic (asking for the microphone) · error. */
export type CallPhase = 'mic' | 'dialing' | 'ringing' | 'connected' | 'machine' | 'vmleave' | 'noanswer' | 'ended' | 'error';

export interface ActiveCall {
  kind: 'cb' | 'ho';
  cbId?: string;
  messageId: string | null;
  name: string;
  number: string;
  language?: string;
  fromLabel?: string;
  phase: CallPhase;
  startedAt: number;
  ringStartedAt?: number;
  connectedAt?: number;
  endedAt?: number;
  muted: boolean;
  held: boolean;
  /** v12.10.30 (#1156): the call back is recorded (Settings › Call Handling › Record call audio). Unknown until the server answers. */
  recorded?: boolean;
  /** 5b: a transfer accepted - the caller is on hold until Resume (H2). */
  waiting?: boolean;
  pad: boolean;
  digits: string;
  outcome?: string | null;
  note?: string | null;
  errorText?: string;
  whisper?: string | null;
  closeIn?: number;
}

export interface TransferInfo {
  state: 'ringing' | 'accepted' | 'none';
  callSid: string;
  messageId?: string | null;
  callerName: string;
  callerPhone: string;
  need: string;
  reference: string;
  language: string;
  agentName: string;
  whisper?: string | null;
  onCallSec: number;
  strategy: string;
  ringSec: number;
  ringLeftSec: number;
  noAnswer: string;
  people: { order: number; name: string; me: boolean; state: string }[];
}

export interface PhoneState {
  status: PhoneStatus | null;
  ready: boolean;
  call: ActiveCall | null;
  transfer: { info: TransferInfo | null; leftSec: number } | null;
}

let state: PhoneState = { status: null, ready: false, call: null, transfer: null };
const listeners = new Set<() => void>();
function set(patch: Partial<PhoneState>) { state = { ...state, ...patch }; listeners.forEach((l) => l()); }
function setCall(patch: Partial<ActiveCall> | null) {
  if (patch === null) { set({ call: null }); return; }
  if (!state.call) return;
  set({ call: { ...state.call, ...patch } });
}
export function getPhone(): PhoneState { return state; }
export function subscribePhone(l: () => void): () => void { listeners.add(l); return () => { listeners.delete(l); }; }

// ── Events the screens listen to ──
/** The call back is written on its message (note, block, Activity) - the drawer reads the message again. detail: { messageId } */
export const CALLBACK_DONE_EVENT = 'vs:callback-done';
/** Open this message in the drawer (5b: the caller's card opens). detail: { id } */
export const OPEN_MESSAGE_EVENT = 'vs:open-message';
function emit(name: string, detail: unknown) { try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch { /* no window */ } }
// 5b: the caller's card to open - kept until Messages is on screen to take it (the overlay can ring on any page).
let pendingOpen: string | null = null;
export function takePendingOpen(): string | null { const v = pendingOpen; pendingOpen = null; return v; }
function openMessage(id: string) { pendingOpen = id; emit(OPEN_MESSAGE_EVENT, { id }); }

/** 1d: "Closing in 5 · 4 · 3 · 2 · 1", then the window closes and the card is as it was (L4). Called once a second by the host. */
export function tickClose() {
  const c = state.call;
  if (!c || c.phase !== 'ended' || typeof c.closeIn !== 'number') return;
  if (c.closeIn <= 1) closeWindow(); else setCall({ closeIn: c.closeIn - 1 });
}

// ── Status / SDK / device ──
let statusPromise: Promise<PhoneStatus | null> | null = null;
export function loadStatus(force = false): Promise<PhoneStatus | null> {
  if (!force && statusPromise) return statusPromise;
  statusPromise = api.get<PhoneStatus>('/t2/voice/status')
    .then((s) => { set({ status: s }); return s; })
    .catch(() => { const s: PhoneStatus = { featureOn: false, configured: false }; set({ status: s }); return s; });
  return statusPromise;
}

let sdkPromise: Promise<TwGlobal> | null = null;
function loadSdk(): Promise<TwGlobal> {
  const w = window as unknown as { Twilio?: TwGlobal };
  if (w.Twilio?.Device) return Promise.resolve(w.Twilio);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<TwGlobal>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SDK_URL;
    s.async = true;
    s.onload = () => (w.Twilio?.Device ? resolve(w.Twilio) : reject(new Error('The phone could not start.')));
    s.onerror = () => { sdkPromise = null; reject(new Error('The phone could not load - check the network and try again.')); };
    document.head.appendChild(s);
  });
  return sdkPromise;
}

async function fetchToken(): Promise<string> {
  try {
    const r = await api.post<{ token: string }>('/t2/voice/token');
    return r.token;
  } catch (e) {
    const msg = e instanceof ApiError && e.body && typeof e.body === 'object' ? (e.body as { message?: string }).message : undefined;
    throw new Error(msg || 'The browser phone could not start - try again.');
  }
}

// ── L5: the Hold mixer. Twilio's AudioProcessor ("Music playback when putting the call on hold" is its documented use) - the stream
//    sent to Twilio is the microphone, or, on Hold, the practice's hold sound. One graph per call; the sound is fetched once. ──
class HoldMixer implements TwAudioProcessor {
  private ctx: AudioContext | null = null;
  private micGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private src: AudioBufferSourceNode | null = null;
  private buffer: AudioBuffer | null = null;
  private loading: Promise<void> | null = null;
  private held = false;

  async createProcessedStream(stream: MediaStream): Promise<MediaStream> {
    const Ctx = (window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    this.ctx = new Ctx();
    const mic = this.ctx.createMediaStreamSource(stream);
    const dest = this.ctx.createMediaStreamDestination();
    this.micGain = this.ctx.createGain();
    this.musicGain = this.ctx.createGain();
    this.micGain.gain.value = this.held ? 0 : 1;
    this.musicGain.gain.value = this.held ? 1 : 0;
    mic.connect(this.micGain).connect(dest);
    this.musicGain.connect(dest);
    void this.loadSound().then(() => this.startMusic());
    return dest.stream;
  }

  async destroyProcessedStream(): Promise<void> {
    try { this.src?.stop(); } catch { /* stopped */ }
    this.src = null;
    try { await this.ctx?.close(); } catch { /* closed */ }
    this.ctx = null; this.micGain = null; this.musicGain = null;
  }

  setHold(on: boolean) {
    this.held = on;
    if (this.micGain) this.micGain.gain.value = on ? 0 : 1;
    if (this.musicGain) this.musicGain.gain.value = on ? 1 : 0;
    if (on) { void this.ctx?.resume(); this.startMusic(); }
  }

  private startMusic() {
    if (!this.ctx || !this.buffer || !this.musicGain || this.src) return;
    this.src = this.ctx.createBufferSource();
    this.src.buffer = this.buffer;
    this.src.loop = true;
    this.src.connect(this.musicGain);
    this.src.start();
  }

  private loadSound(): Promise<void> {
    if (this.buffer || this.loading) return this.loading ?? Promise.resolve();
    this.loading = (async () => {
      try {
        const token = localStorage.getItem('vs_t2_token');
        const res = await fetch(buildUrl('/api/t2/voice/hold-sound'), { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include' });
        if (!res.ok || !this.ctx) return;
        this.buffer = await this.ctx.decodeAudioData(await res.arrayBuffer());
      } catch { /* no sound - Hold mutes the microphone only */ }
      finally { this.loading = null; }
    })();
    return this.loading;
  }
}

const mixer = new HoldMixer();

let device: TwDevice | null = null;
let devicePromise: Promise<TwDevice> | null = null;
let activeTw: TwCall | null = null;
let incomingTw: TwCall | null = null;

async function ensureDevice(register: boolean): Promise<TwDevice> {
  if (device) { if (register && device.state !== 'registered') await device.register().catch(() => undefined); return device; }
  if (devicePromise) return devicePromise;
  devicePromise = (async () => {
    const tw = await loadSdk();
    const token = await fetchToken();
    const d = new tw.Device(token, { logLevel: 'error', codecPreferences: ['opus', 'pcmu'], closeProtection: 'A call is still on - leave anyway?' });
    d.on('tokenWillExpire', () => { void fetchToken().then((t) => d.updateToken(t)).catch(() => undefined); });   // #1155: renewed before it expires
    d.on('registered', () => { set({ ready: true }); void beat(); });
    d.on('unregistered', () => { set({ ready: false }); void beat(); });
    d.on('error', (err: unknown) => { console.warn('browser phone', err); });
    d.on('incoming', (call: unknown) => onIncoming(call as TwCall));
    try { await d.audio?.addProcessor(mixer); } catch { /* no processor: Hold mutes the microphone only */ }
    device = d;
    if (register) await d.register().catch(() => undefined);
    return d;
  })();
  try { return await devicePromise; } finally { devicePromise = null; }
}

// ── Presence (#1157): only people whose browser phone is READY and not on a call are rung (Q11). ──
let beatTimer: number | null = null;
async function beat() {
  const ready = !!device && device.state === 'registered' && micState !== 'denied';
  try { await api.post('/t2/voice/presence', { ready, onCall: !!state.call && state.call.phase !== 'ended' && state.call.phase !== 'noanswer' }); } catch { /* next beat */ }
}

/** Start the phone for a Handoff person at sign-in (registered, so transfers can ring it). Safe to call repeatedly. */
export async function startForHandoff(): Promise<void> {
  const s = await loadStatus();
  if (!s?.featureOn || !s.configured || !s.inHandoff) return;
  try {
    await ensureDevice(true);
    if (beatTimer === null) beatTimer = window.setInterval(() => void beat(), 25000);
    void beat();
  } catch { /* the Call back button still explains what is wrong when pressed */ }
}

export function stopPhone() {
  if (beatTimer !== null) { window.clearInterval(beatTimer); beatTimer = null; }
  try { device?.destroy(); } catch { /* gone */ }
  device = null;
  set({ ready: false, call: null, transfer: null });
  void api.post('/t2/voice/presence', { ready: false, onCall: false }).catch(() => undefined);
}

// ── The microphone (#1155: "First use asks for the microphone with a plain explanation; a blocked microphone or no audio device shows
//    how to fix it - never a silent failure.") ──
let micState: 'unknown' | 'granted' | 'denied' | 'prompt' = 'unknown';
export const MIC_BLOCKED = 'Your microphone is blocked. Click the lock icon in the address bar › Microphone › Allow, then try again.';
export const MIC_NONE = 'No microphone found. Plug in a headset (or turn on the microphone) and try again.';
async function ensureMic(): Promise<string | null> {
  try {
    const p = await (navigator.permissions?.query({ name: 'microphone' as PermissionName }) ?? Promise.resolve(null));
    if (p) micState = p.state as typeof micState;
  } catch { /* Firefox / Safari - ask below */ }
  if (micState === 'denied') return MIC_BLOCKED;
  try {
    const devs = await navigator.mediaDevices?.enumerateDevices?.();
    if (devs && devs.length > 0 && !devs.some((d) => d.kind === 'audioinput')) return MIC_NONE;
  } catch { /* ask below */ }
  if (micState === 'granted') return null;
  try {
    const s = await navigator.mediaDevices.getUserMedia({ audio: true });
    s.getTracks().forEach((t) => t.stop());
    micState = 'granted';
    return null;
  } catch (e) {
    const name = (e as { name?: string })?.name ?? '';
    if (name === 'NotFoundError' || name === 'OverconstrainedError') return MIC_NONE;
    micState = 'denied';
    return MIC_BLOCKED;
  }
}

function twilioError(e: unknown): string {
  const code = (e as { code?: number })?.code;
  if (code === 31401 || code === 31208 || code === 31402) return MIC_BLOCKED;
  if (code === 20101 || code === 20104 || code === 31204 || code === 31205) return 'The phone pass expired - press Call back again.';
  if (code && code >= 31000 && code < 31010) return 'The connection dropped - check the network and try again.';
  return (e as { message?: string })?.message || 'The call could not be placed.';
}

function newId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  return c?.randomUUID ? c.randomUUID() : `cb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// ═════════════════════════════ #1156 CALL BACK ═════════════════════════════
export interface CallBackTarget { messageId: string; to: string; name: string; language?: string }
let lastTarget: CallBackTarget | null = null;
let pollTimer: number | null = null;

/** E.164 for a US / Canada number, else null (L2: "Another number" has the +1 mask). */
export function toE164(raw: string): string | null {
  let d = (raw ?? '').replace(/\D+/g, '');
  if (d.length === 10) d = '1' + d;
  return d.length === 11 && d[0] === '1' && d[1] >= '2' ? `+${d}` : null;
}

export async function startCallBack(t: CallBackTarget): Promise<void> {
  if (state.call && state.call.phase !== 'ended' && state.call.phase !== 'noanswer' && state.call.phase !== 'error') return;
  const to = toE164(t.to);
  lastTarget = t;
  const base: ActiveCall = { kind: 'cb', messageId: t.messageId, name: t.name, number: t.to, language: t.language, phase: 'mic', startedAt: Date.now(), muted: false, held: false, pad: false, digits: '' };
  set({ call: base });
  if (!to) { setCall({ phase: 'error', errorText: 'That number cannot be called - enter 10 digits.' }); return; }
  const s = await loadStatus();
  if (!s?.featureOn) { setCall(null); return; }
  if (!s.configured) { setCall({ phase: 'error', errorText: s.reason || 'Call back is not set up yet - VoiceSpectrum Support' }); return; }
  void api.get<{ number?: string; label?: string }>(`/t2/voice/caller-id?messageId=${encodeURIComponent(t.messageId)}`)
    .then((r) => setCall({ fromLabel: r?.label && r?.number ? `${r.label} · ${fmtPhone(r.number)}` : r?.label }))
    .catch(() => undefined);
  const micProblem = await ensureMic();
  if (micProblem) { setCall({ phase: 'error', errorText: micProblem }); return; }
  setCall({ phase: 'dialing' });
  try {
    const d = await ensureDevice(!!s.inHandoff);
    const cbId = newId();
    setCall({ cbId });
    const call = await d.connect({ params: { To: to, MessageId: t.messageId, Cb: cbId, Kind: 'callback' } });
    activeTw = call;
    call.on('ringing', () => { if (state.call?.phase === 'dialing') setCall({ phase: 'ringing', ringStartedAt: Date.now() }); });
    call.on('accept', () => setCall({ phase: 'connected', connectedAt: Date.now() }));
    call.on('disconnect', () => void afterCallBack(cbId));
    call.on('cancel', () => void afterCallBack(cbId));
    call.on('error', (e: unknown) => { if (state.call?.cbId === cbId && state.call.phase !== 'connected') setCall({ errorText: twilioError(e) }); });
    pollCall(cbId);
    void beat();
  } catch (e) {
    setCall({ phase: 'error', errorText: e instanceof Error ? e.message : twilioError(e) });
  }
}

/** The window follows the server's view of the call: R4 voicemail detection (machine -> 3b) while it runs. */
function pollCall(cbId: string) {
  if (pollTimer !== null) window.clearInterval(pollTimer);
  pollTimer = window.setInterval(async () => {
    const c = state.call;
    if (!c || c.cbId !== cbId) { if (pollTimer !== null) window.clearInterval(pollTimer); pollTimer = null; return; }
    try {
      const r = await api.get<{ known: boolean; phase?: string; machine?: boolean; final?: boolean; recorded?: boolean }>(`/t2/voice/calls/${encodeURIComponent(cbId)}`);
      if (!r?.known) return;
      if (typeof r.recorded === 'boolean' && state.call?.cbId === cbId && state.call.recorded !== r.recorded) setCall({ recorded: r.recorded });
      if (r.machine && state.call?.phase === 'connected') setCall({ phase: 'machine' });
      if (r.phase === 'ringing' && state.call?.phase === 'dialing') setCall({ phase: 'ringing', ringStartedAt: Date.now() });
    } catch { /* next tick */ }
  }, 1500);
}

async function afterCallBack(cbId: string) {
  if (pollTimer !== null) { window.clearInterval(pollTimer); pollTimer = null; }
  activeTw = null;
  mixer.setHold(false);
  const c = state.call;
  if (!c || c.cbId !== cbId || c.phase === 'ended' || c.phase === 'noanswer') return;
  const wasConnected = !!c.connectedAt;
  const secs = c.connectedAt ? Math.round((Date.now() - c.connectedAt) / 1000) : 0;
  if (c.phase === 'error') { void finishOnServer(cbId, wasConnected, secs); return; }
  setCall({ endedAt: Date.now() });
  // Twilio's ending (leg status) normally lands within a second or two - wait for it, then show the right end state.
  let outcome: string | null = null; let note: string | null = null;
  for (let i = 0; i < 8 && !outcome; i++) {
    await new Promise((r) => window.setTimeout(r, 1000));
    try {
      const r = await api.get<{ known: boolean; final?: boolean; outcome?: string; note?: string }>(`/t2/voice/calls/${encodeURIComponent(cbId)}`);
      if (r?.final) { outcome = r.outcome ?? null; note = r.note ?? null; }
    } catch { /* retry */ }
  }
  if (!outcome) { const r = await finishOnServer(cbId, wasConnected, secs); outcome = r.outcome; note = r.note; }
  if (state.call?.cbId !== cbId) return;
  // 3a: "Rang 30 s. Same format: Close or Call again. Nothing is queued (R3)." Everything else: 1d "Closing in 5…1".
  if (outcome === 'no-answer' || outcome === 'busy') setCall({ phase: 'noanswer', outcome, note });
  else setCall({ phase: 'ended', outcome, note, closeIn: 5 });
  emit(CALLBACK_DONE_EVENT, { messageId: c.messageId });
  void beat();
}

async function finishOnServer(cbId: string, connected: boolean, secs: number): Promise<{ outcome: string | null; note: string | null }> {
  try {
    const r = await api.post<{ outcome?: string; note?: string }>(`/t2/voice/calls/${encodeURIComponent(cbId)}/end`, { status: connected ? 'completed' : 'canceled', seconds: secs });
    return { outcome: r?.outcome ?? null, note: r?.note ?? null };
  } catch { return { outcome: null, note: null }; }
}

/** Cancel (dialing / ringing) or Hang up (connected / voicemail) - the same thing to Twilio. */
export function hangUp() {
  const c = state.call;
  if (!c) return;
  if (c.phase === 'error' || c.phase === 'mic') { closeWindow(); return; }
  if (c.kind === 'ho') {
    try { activeTw?.disconnect(); } catch { /* gone */ }
    activeTw = null; mixer.setHold(false);
    setCall({ phase: 'ended', endedAt: Date.now(), note: `Took the live transfer - ${clock(Math.round((Date.now() - (c.connectedAt ?? Date.now())) / 1000))}.`, closeIn: 5 });
    void beat();
    return;
  }
  try { activeTw?.disconnect(); } catch { void afterCallBack(c.cbId ?? ''); }
}

export function closeWindow() { setCall(null); void beat(); }
export function callAgain() { if (lastTarget) { set({ call: null }); void startCallBack(lastTarget); } }

export function toggleMute() {
  const c = state.call; if (!c || !activeTw) return;
  const next = !c.muted;
  if (!c.held) activeTw.mute(next);
  setCall({ muted: next });
}

/** L5 / Q16: Hold - the patient hears the practice hold sound, the staff mic is muted; Resume brings them back. */
export function toggleHold() {
  const c = state.call; if (!c || !activeTw) return;
  const next = !c.held;
  mixer.setHold(next);
  activeTw.mute(next ? false : c.muted);   // while held, the processed stream (the hold sound) must flow
  setCall({ held: next, waiting: next ? c.waiting : false });
}

export function togglePad() { const c = state.call; if (c) setCall({ pad: !c.pad }); }
/** Q15: digits are sent as tones and kept in the strip for this call only (not saved). */
export function sendDigit(k: string) {
  const c = state.call; if (!c || !activeTw) return;
  try { activeTw.sendDigits(k); } catch { /* not connected */ }
  setCall({ digits: (c.digits + k).slice(-24) });
}

/** 3b "Leave voicemail" - staff talk; the note will say "left a message". */
export function leaveVoicemail() {
  const c = state.call; if (!c?.cbId) return;
  void api.post(`/t2/voice/calls/${encodeURIComponent(c.cbId)}/leave-voicemail`).catch(() => undefined);
  setCall({ phase: 'vmleave' });
}

// ═════════════════════════════ #1157 LIVE TRANSFER ═════════════════════════════
let transferTimer: number | null = null;

async function onIncoming(call: TwCall) {
  if (state.call && state.call.phase !== 'ended' && state.call.phase !== 'noanswer' && state.call.phase !== 'error') { try { call.reject(); } catch { /* gone */ } return; }
  incomingTw = call;
  set({ transfer: { info: null, leftSec: state.status?.ringSec ?? 20 } });
  call.on('cancel', () => closeTransfer());     // taken by someone else, the caller hung up, or the ring time ran out
  call.on('disconnect', () => { if (incomingTw === call) closeTransfer(); });
  call.on('reject', () => closeTransfer());
  // what the overlay shows: who is calling and what they need (the browser rings first - the details follow a moment later)
  for (let i = 0; i < 8; i++) {
    try {
      const info = await api.get<TransferInfo>('/t2/voice/transfers/mine');
      if (incomingTw !== call) return;
      if (info?.state === 'ringing') { set({ transfer: { info, leftSec: info.ringLeftSec || info.ringSec } }); break; }
    } catch { /* retry */ }
    await new Promise((r) => window.setTimeout(r, 600));
  }
  if (transferTimer !== null) window.clearInterval(transferTimer);
  transferTimer = window.setInterval(() => {
    const t = state.transfer; if (!t) { if (transferTimer !== null) window.clearInterval(transferTimer); transferTimer = null; return; }
    const left = Math.max(0, t.leftSec - 1);
    set({ transfer: { ...t, leftSec: left } });
    if (left <= 0) closeTransfer();   // at 0 the overlay simply closes (v1.7) - Twilio ends the leg on its own ring timeout
  }, 1000);
  try {
    if (document.hidden && 'Notification' in window && Notification.permission === 'granted')
      new Notification('Incoming call transfer', { body: state.transfer?.info?.callerName || 'A caller is being transferred to you' });
  } catch { /* no notifications */ }
}

function closeTransfer() {
  if (transferTimer !== null) { window.clearInterval(transferTimer); transferTimer = null; }
  incomingTw = null;
  set({ transfer: null });
}

/** H2: Accept = taken by that person; the caller's card opens with the call ON HOLD - only Resume + Hang up until Resume. */
export async function acceptTransfer() {
  const call = incomingTw; const t = state.transfer;
  if (!call || !t) return;
  const micProblem = await ensureMic();
  if (micProblem) { try { call.reject(); } catch { /* gone */ } closeTransfer(); set({ call: { kind: 'ho', messageId: null, name: t.info?.callerName || 'Caller', number: t.info?.callerPhone || '', phase: 'error', errorText: micProblem, startedAt: Date.now(), muted: false, held: false, pad: false, digits: '' } }); return; }
  mixer.setHold(true);   // the caller hears the hold sound from the first moment
  const info = t.info;
  closeTransfer();
  incomingTw = null;
  activeTw = call;
  try { call.accept(); } catch { /* the leg is gone - disconnect below closes it */ }
  set({ call: {
    kind: 'ho', messageId: info?.messageId ?? null, name: info?.callerName || fmtPhone(info?.callerPhone ?? '') || 'Caller', number: info?.callerPhone ?? '',
    language: info?.language, fromLabel: `Transfer from ${info?.agentName || 'the AI assistant'}`, phase: 'connected', startedAt: Date.now(), connectedAt: Date.now(),
    muted: false, held: true, waiting: true, pad: false, digits: '', whisper: info?.whisper ?? null,
  } });
  call.on('disconnect', () => { if (state.call?.kind === 'ho' && state.call.phase !== 'ended') { const c = state.call; mixer.setHold(false); activeTw = null; setCall({ phase: 'ended', endedAt: Date.now(), note: `Took the live transfer - ${clock(Math.round((Date.now() - (c.connectedAt ?? Date.now())) / 1000))}.`, closeIn: 5 }); void beat(); } });
  if (info?.messageId) openMessage(info.messageId);
  // the whisper text for the window (5b) arrives once the server has recorded the accept
  window.setTimeout(() => { void api.get<TransferInfo>('/t2/voice/transfers/mine').then((r) => { if (r?.state === 'accepted' && state.call?.kind === 'ho') setCall({ whisper: r.whisper ?? null, messageId: state.call.messageId ?? r.messageId ?? null }); if (r?.messageId && !info?.messageId) openMessage(r.messageId); }).catch(() => undefined); }, 1500);
  void beat();
}

/** Skip (Esc): only me - the others keep ringing (all at once) or the next person is rung now (one by one) (Q9). */
export function skipTransfer() {
  const call = incomingTw;
  closeTransfer();
  try { call?.reject(); } catch { /* gone */ }
}

/** 5b Resume: the caller hears staff - the hold sound stops. */
export function resumeTransfer() { const c = state.call; if (c?.waiting || c?.held) toggleHold(); setCall({ waiting: false }); }

// ── helpers ──
export function clock(s: number): string { const v = Math.max(0, Math.floor(s)); return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}`; }
export function fmtPhone(p: string): string {
  const d = (p ?? '').replace(/\D+/g, '');
  const n = d.length === 11 && d[0] === '1' ? d.slice(1) : d;
  return n.length === 10 ? `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}` : p;
}

